// A presentation adapter for existing plain-text lessons; Markdown parsing stays
// with remark. No content is generated, summarized, fetched, or saved here.
export function formatLessonText(content, kind) {
  if (typeof content !== 'string') return typeof content === 'number' ? String(content) : JSON.stringify(content ?? '', null, 2);
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  let fenced = false;
  return lines.map((line, index) => {
    const text = line.trim();
    if (/^\s*(```|~~~)/.test(line)) { fenced = !fenced; return line; }
    if (fenced || /^(?:\s{4}|\t|\s*[#>|])/.test(line)) return line;
    const next = lines.slice(index + 1).find(item => item.trim())?.trim() || '';
    if (/^عنوان الدرس\s*[:：]/.test(text)) return `# ${text}`;
    const numbered = text.match(/^[0-9]+[).]\s+(.+)$/);
    const boundary = index === 0 || !lines[index - 1].trim();
    const section = /^(?:ال)?(?:مقدمة|أهداف|شرح|حل |أسئلة|ملخص|خلاصة|تحضير|خطوات|أنشطة|نشاط|تقييم|تقويم|أخطاء|خاتمة|تمارين|طرق)/;
    const term = kind === 'terms' && /^(?:المعنى|التعريف)\s*[:：]/.test(next);
    if (numbered && boundary && text.length < 140 && (section.test(numbered[1]) || term)) return `\n## ${text}\n`;
    const subheading = /^(?:(?:أول|ثان|ثالث|رابع|خامس|سادس|سابع|ثامن|تاسع|عاشر)[اًيا\s]*[:：]?\s|أهداف الدرس|الهدف ببساطة|الخطوة [0-9]+|التمرين [0-9]+|أمثلة إضافية|أهم النقاط|أخطاء شائعة)/;
    if (boundary && text.length < 125 && subheading.test(text)) return `\n### ${text}\n`;
    if (/^(?:انتبه|لاحظ|ملاحظة|تنبيه|نصيحة|الإجابة|الجواب|الحل|المعنى|التعريف)\s*[:：]/.test(text)) return `\n${line}\n`;
    return line.replace(/^([0-9]+)،\s*/, '$1. ');
  }).join('\n');
}

const nodeText = node => node.value || (node.children || []).map(nodeText).join('');
const properties = node => { node.data ||= {}; node.data.hProperties ||= {}; return node.data.hProperties; };
const wrap = (children, className, extra = {}) => ({ type: 'lessonContainer', children, data: { hName: 'div', hProperties: { className, ...extra } } });

function topicBlocks(children) {
  const result = []; let current = [];
  const flush = () => { if (current.length) result.push(wrap(current, 'lesson-reading-block')); current = []; };
  children.forEach(node => {
    if (node.type === 'heading' && node.depth === 3) flush();
    current.push(node);
  });
  flush();
  return result;
}

export function remarkLessonDesign({ prefix }) {
  return tree => {
    let count = 0; let sections = 0;
    const visit = node => {
      if (['heading', 'paragraph', 'listItem', 'link', 'table', 'blockquote'].includes(node.type)) {
        const props = properties(node);
        props['data-testid'] = `${prefix}-${node.type.toLowerCase()}-${count++}`;
        if (node.type === 'paragraph') {
          const text = nodeText(node).trim();
          const kind = /^(?:انتبه|لاحظ|ملاحظة|تنبيه|نصيحة|خلّيني|شو رأيك)/.test(text) ? 'note'
            : /^(?:الإجابة|الجواب|الحل)\s*[:：]/.test(text) ? 'answer'
              : /^(?:المعنى|التعريف)\s*[:：]/.test(text) || /^[^\n:]{1,35}(?:يعني|هو)\s*[:：]/.test(text) ? 'definition' : null;
          if (kind) props.className = `lesson-reading-${kind}`;
        }
        if (node.type === 'heading' && node.depth === 2) {
          sections += 1;
          const first = node.children?.[0];
          const marker = first?.type === 'text' && first.value.match(/^([0-9]+)[).]\s+/);
          props['data-section-number'] = marker ? marker[1] : String(sections);
          props['data-section-tone'] = ['rose', 'sky', 'sun', 'leaf'][(sections - 1) % 4];
          if (marker) first.value = first.value.slice(marker[0].length);
        }
      }
      node.children?.forEach(visit);
    };
    visit(tree);
    const result = []; let intro = []; let current = null;
    const flushIntro = () => { if (intro.length) result.push(wrap(intro, 'lesson-reading-intro')); intro = []; };
    const flushSection = () => {
      if (current) result.push(wrap([current.heading, ...topicBlocks(current.children)], 'lesson-reading-part', { 'data-tone': properties(current.heading)['data-section-tone'] }));
      current = null;
    };
    tree.children.forEach(node => {
      if (node.type === 'heading' && node.depth <= 2) {
        flushIntro(); flushSection();
        if (node.depth === 1) result.push(node);
        else current = { heading: node, children: [] };
      } else if (current) current.children.push(node);
      else intro.push(node);
    });
    flushIntro(); flushSection(); tree.children = result;
  };
}